/**
 * LinksList Component
 *
 * List view for displaying shared links from message history
 */

import React from 'react';
import type { SearchResult } from '../../services/search.service';
import { getDisplayUrl } from '@/utils/url.util';
import { stripMentionHtml } from '@/utils/linkify.util';

interface LinksListProps {
  messages: SearchResult[];
  scope: { channelId?: string; dmThreadId?: string };
}

export const LinksList: React.FC<LinksListProps> = ({ messages }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('bg-BG', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Extract all links from messages
  const linkItems = messages.flatMap((message) =>
    (message.message_links || []).map((link) => ({
      ...link,
      message,
    }))
  );

  if (linkItems.length === 0) {
    return null;
  }

  return (
    <div className="links-list">
      {linkItems.map((item, index) => (
        <div key={`${item.id}-${index}`} className="link-card">
          <div className="link-header">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="link-url"
            >
              🔗 {getDisplayUrl(item.url)}
            </a>
          </div>

          {(item.title || item.description) && (
            <div className="link-preview">
              {item.favicon_url && (
                <img src={item.favicon_url} alt="" className="link-favicon" />
              )}
              <div className="link-content">
                {item.title && <h4 className="link-title">{item.title}</h4>}
                {item.description && (
                  <p className="link-description">{item.description}</p>
                )}
              </div>
            </div>
          )}

          <div className="link-meta">
            <span className="link-sender">
              Shared by{' '}
              {item.message.users.display_name ||
                `${item.message.users.first_name} ${item.message.users.last_name}`}
            </span>
            <span className="link-date">{formatDate(item.message.created_at)}</span>
          </div>

          {item.message.content && (
            <div className="link-message-content">
              <p>{stripMentionHtml(item.message.content)}</p>
            </div>
          )}
        </div>
      ))}

      <style>{`
        .links-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .link-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1rem;
          transition: box-shadow 0.2s;
        }

        .link-card:hover {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .link-header {
          margin-bottom: 0.75rem;
        }

        .link-url {
          color: #3b82f6;
          text-decoration: none;
          font-size: 0.875rem;
          word-break: break-all;
        }

        .link-url:hover {
          text-decoration: underline;
        }

        .link-preview {
          display: flex;
          gap: 0.75rem;
          padding: 0.75rem;
          background: #f9fafb;
          border-radius: 0.375rem;
          margin-bottom: 0.75rem;
        }

        .link-favicon {
          width: 1.5rem;
          height: 1.5rem;
          flex-shrink: 0;
        }

        .link-content {
          flex: 1;
          min-width: 0;
        }

        .link-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
        }

        .link-description {
          margin: 0.25rem 0 0;
          font-size: 0.875rem;
          color: #6b7280;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .link-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.75rem;
          color: #6b7280;
        }

        .link-sender {
          font-weight: 500;
        }

        .link-message-content {
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #e5e7eb;
        }

        .link-message-content p {
          margin: 0;
          font-size: 0.875rem;
          color: #4b5563;
        }

        @media (max-width: 768px) {
          .link-meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
          }
        }
      `}</style>
    </div>
  );
};
